import { Link } from "react-router-dom";
import { ArrowRight, Star, MapPin, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useBooking } from "@/components/booking";
import { BUSINESS, SERVICES, IMAGES } from "@/data/business";

const FACTS = [
  { icon: Star, label: `${BUSINESS.rating} ★ Google rating`, sub: `${BUSINESS.reviewCount} reviews` },
  { icon: Clock, label: "Open daily", sub: BUSINESS.hoursShort },
  { icon: MapPin, label: "Patel Nagar / Hardattpur", sub: "Varanasi, Uttar Pradesh" },
];

export default function About() {
  const { openBooking } = useBooking();
  return (
    <main className="bg-ivory">
      <SEO
        title="About Us"
        description="About Glam Girl Makeup Studio — a ladies salon and hair & makeup studio serving Patel Nagar / Hardattpur, Varanasi. Rated 4.8 on Google from 56 reviews. Open daily 10 AM – 8 PM."
      />
      <section className="grain mx-auto max-w-7xl px-5 pb-20 pt-32 sm:px-8 lg:pt-40" data-testid="about-hero">
        <div className="grid items-end gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <Eyebrow>About Us</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              About Glam Girl <em className="text-rose">Makeup Studio.</em>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-charcoal/65">
              Glam Girl Makeup Studio is a ladies salon and hair &amp; makeup studio
              serving the Patel Nagar / Hardattpur area of Varanasi. We keep things
              simple: careful work, a clean and comfortable space, and service that
              our clients rate 4.8 stars on Google across {BUSINESS.reviewCount} reviews.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-charcoal/65">
              From everyday haircuts and blowdries to keratin treatments, colour,
              braids, makeup and bridal services — everything happens in one calm,
              warmly lit studio.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-2xl border border-charcoal/10 shadow-[0_24px_60px_rgba(27,25,22,0.12)]">
              <img
                src={IMAGES.salonInterior.src}
                alt={IMAGES.salonInterior.alt}
                className="aspect-[4/3] w-full object-cover"
                fetchpriority="high"
              />
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-5 sm:grid-cols-3">
          {FACTS.map((f, i) => (
            <Reveal key={f.label} delay={0.06 * i}>
              <div className="flex items-center gap-4 rounded-2xl border border-charcoal/10 bg-white p-6" data-testid={`about-fact-${i}`}>
                <f.icon className="h-5 w-5 shrink-0 text-rose" />
                <div>
                  <p className="text-sm font-bold text-charcoal">{f.label}</p>
                  <p className="text-xs text-charcoal/55">{f.sub}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="about-space" className="border-y border-charcoal/10 bg-cream">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:py-28">
          <Reveal>
            <div className="arch mx-auto max-w-sm overflow-hidden border border-rose/20">
              <img src={IMAGES.bridalMirror.src} alt={IMAGES.bridalMirror.alt} loading="lazy" className="aspect-[3/4] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Our Space</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
              Warm light, clean stations, no rush.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-charcoal/65">
              The studio is set up for comfort — LED arch mirrors, organised styling
              stations and a hygienic environment that reviewers mention again and
              again. Walk in, sit down, and take your time.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-services" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-28">
        <Reveal>
          <Eyebrow>Our Services</Eyebrow>
          <h2 className="mt-4 max-w-lg font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
            Seven categories under one roof.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-x-10 sm:grid-cols-2">
          {SERVICES.map((c, i) => (
            <Reveal key={c.id} delay={0.04 * i}>
              <Link
                to={`/services#${c.id}`}
                data-testid={`about-service-${c.id}`}
                className="group flex items-baseline justify-between border-b border-charcoal/10 py-5 transition-colors hover:border-rose/40"
              >
                <span className="font-serif text-xl font-medium text-charcoal transition-colors group-hover:text-rose-deep sm:text-2xl">
                  {c.name}
                </span>
                <span className="flex items-center gap-3 text-xs text-charcoal/45">
                  {c.items.length} service{c.items.length > 1 ? "s" : ""}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="about-visit" className="grain relative bg-ink text-ivory">
        <div className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8 lg:py-28">
          <Reveal>
            <Eyebrow dark>Visit Us</Eyebrow>
            <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
              Refined beauty, <em className="text-champagne">close to home.</em>
            </h2>
            <p className="mx-auto mt-6 max-w-lg text-sm leading-relaxed text-ivory/55">{BUSINESS.address}</p>
            <p className="mt-3 text-sm font-semibold text-champagne">{BUSINESS.hoursLine}</p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <button
                onClick={() => openBooking()}
                data-testid="about-book-button"
                className="rounded-full bg-ivory px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-colors hover:bg-champagne"
              >
                Book Appointment
              </button>
              <Link
                to="/visit"
                data-testid="about-visit-link"
                className="rounded-full border border-ivory/25 px-8 py-4 text-sm font-semibold tracking-wide text-ivory transition-colors hover:border-champagne hover:text-champagne"
              >
                Plan Your Visit
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
